// =============================================
// Attachments of a vendor / customer / other party  (acctg_party_attachment, migration 125)
// =============================================
// Account approval documents, credit applications, agreements — the base documents kept
// with a party. A picked file goes to the server blob store straight away (File Storage
// Rules: nothing lives only in the browser); the form keeps the returned pointers and
// writes the rows when the party itself is saved, so a new party can have attachments
// before it has an id.
// =============================================
import React, { useCallback, useRef, useState } from 'react';
import { FiPaperclip, FiTrash2 } from 'react-icons/fi';
import { putServerBlob, getServerBlobUrl } from '../../../lib/serverBlobStore';
import PartyTip from './PartyTip';

export interface AttachmentLine {
  id: number | null;        // null = uploaded, row not written yet
  name: string;
  path: string;             // blob:parties/<acct>/<uuid>.<ext>
  mime: string;
  size: number;
  removed?: boolean;
}

interface PartyAttachmentsProps {
  lines: AttachmentLine[];
  onChange: (next: AttachmentLine[]) => void;
  accountOwnerId: number;
}

const MAX_MB = 25;
const sizeLabel = (n: number) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const PartyAttachments: React.FC<PartyAttachmentsProps> = ({ lines, onChange, accountOwnerId }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const addFiles = useCallback(async (files: FileList | null) => {
    if (!files || !files.length) return;
    setBusy(true); setMsg('');
    const added: AttachmentLine[] = [];
    const failed: string[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_MB * 1048576) { failed.push(`${f.name} (over ${MAX_MB} MB)`); continue; }
      const ext = (f.name.includes('.') ? f.name.split('.').pop() || '' : '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) || 'bin';
      const key = await putServerBlob(f, { scope: 'parties', accountOwnerId, ext });
      if (key) added.push({ id: null, name: f.name.slice(0, 255), path: key, mime: f.type || '', size: f.size });
      else failed.push(f.name);
    }
    if (added.length) onChange([...lines, ...added]);
    setMsg(failed.length ? `Could not upload ${failed.join(', ')} — check that you are signed in and online.` : '');
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  }, [lines, onChange, accountOwnerId]);

  const open = useCallback(async (l: AttachmentLine) => {
    const url = await getServerBlobUrl(l.path);
    if (!url) { setMsg(`Could not open ${l.name}.`); return; }
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);

  const remove = (index: number) => onChange(lines[index].id == null
    ? lines.filter((_, i) => i !== index)
    : lines.map((l, i) => (i === index ? { ...l, removed: true } : l)));

  const shown = lines.map((l, i) => ({ l, i })).filter(x => !x.l.removed);

  return (
    <div className="pm-field pm-span-4">
      <span className="pm-label">Attachments</span>
      <PartyTip text="Account approval documents or base documents relating to this party. Kept when you save." />
      <div className="pm-attachments">
        {shown.map(({ l, i }) => (
          <span className="pm-attachment" key={l.id != null ? `s${l.id}` : `n${l.path}`}>
            <button type="button" className="pm-link-btn" title={`Open ${l.name}`} onClick={() => open(l)}>
              <FiPaperclip size={14} /> {l.name}
            </button>
            <span className="pm-hint">{sizeLabel(l.size)}</span>
            <button type="button" className="pm-icon-btn pm-danger" title={`Remove ${l.name}`} onClick={() => remove(i)}><FiTrash2 size={14} /></button>
          </span>
        ))}
        <button type="button" className="pm-btn" disabled={busy} onClick={() => inputRef.current?.click()}>
          <FiPaperclip size={15} /> {busy ? 'Uploading…' : 'Add attachment'}
        </button>
        <input ref={inputRef} type="file" multiple hidden onChange={e => addFiles(e.target.files)} />
      </div>
      {msg && <span className="pm-error">{msg}</span>}
    </div>
  );
};

export default PartyAttachments;
